/* eslint-disable import/no-unused-modules */
import _ from 'lodash';
import {
  getTealiumRedesignEvents,
  getRecommendationIdDataForItem,
  setTealiumSourceCollectionGlobal,
  getCollectionCount,
  getCollectionPosition,
} from '@foxcorp/delta-web-analytics';
import { helperGetApiHeaders, helperConvertApiRoute } from 'helpers/ApiHelper';
import { setSuggestedChannel } from 'screens/Live/LiveActions';
import CarouselTheme from 'screens/Collection/Carousel.module.scss';
import CarouselTileTheme from 'screens/Collection/CarouselTile.css';
import CarouselPosterStyle from 'screens/Collection/CarouselPoster.module.scss';
import { clearVideo as removeGlobalVideoPlayer } from 'components/VideoPlayerGlobal/Actions';
import {
  isMobileBreakpoint,
  SITE_PLATFORM_MOBILE,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  defaultPosterResponsiveness,
} from 'helpers/BreakpointHelper';
import {
  calculateNextRefreshForPanel,
  isClip,
  isLive,
  isSportsContent,
} from 'helpers/ContentHelper';
import { PAGE_COLLECTION } from 'helpers/PageHelper';
import { helperGoToRelativeUrl, helperGetLiveUrl } from 'helpers/RouteHelper';
import { thumbnailNetworkLogoEnabled, autoplayEnabled } from 'helpers/ConfigHelper';
import { homeCarouselVideoProcessor } from 'helpers/Processor/CarouselVideoProcessor';
import { COLLECTION_THUMBNAIL_IMAGE, renderImage } from 'helpers/ImageHelper';

export const BREAKPOINTS = (() => {
  const breakpoints = {
    [SITE_PLATFORM_WEB_LARGE]: {
      addCarouselPlaceholders: true,
      allowScrollLock: false,
      videoBackground: true,
      smallPromoImage: 'images.desktop-promo',
      allowInfoToggle: false,
      showLiveButtons: true,
      carouselLimit: 5,
      slidesToScroll: 5,
      posterLimit: 5,
      posterToScroll: 5,
      descriptionLines: 4,
      roundArrows: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      showCarouselArrows: true,
      showTileMetadataOnHover: true,
      useMobileUpcomingOverlay: false,
      fycLogoHeight: 80,
      useScrollableDiv: false,
    },
    [SITE_PLATFORM_WEB]: {
      addCarouselPlaceholders: true,
      allowScrollLock: false,
      videoBackground: true,
      smallPromoImage: 'images.desktop-promo',
      allowInfoToggle: false,
      showLiveButtons: true,
      carouselLimit: 4,
      slidesToScroll: 4,
      posterLimit: 5,
      posterToScroll: 5,
      descriptionLines: 3,
      roundArrows: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      showCarouselArrows: true,
      showTileMetadataOnHover: true,
      useMobileUpcomingOverlay: false,
      fycLogoHeight: 80,
      useScrollableDiv: false,
    },
    [SITE_PLATFORM_TABLET]: {
      addCarouselPlaceholders: true,
      allowScrollLock: false,
      videoBackground: true,
      smallPromoImage: 'images.desktop-promo',
      allowInfoToggle: true,
      showLiveButtons: true,
      carouselLimit: 2,
      slidesToScroll: 2,
      posterLimit: 4,
      posterToScroll: 4,
      descriptionLines: 2,
      roundArrows: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      showCarouselArrows: true,
      showTileMetadataOnHover: true,
      useMobileUpcomingOverlay: true,
      fycLogoHeight: 80,
      useScrollableDiv: false,
    },
    [SITE_PLATFORM_MOBILE]: {
      addCarouselPlaceholders: false,
      allowScrollLock: true,
      videoBackground: false,
      smallPromoImage: 'images.mobile-promo',
      allowInfoToggle: true,
      showLiveButtons: true,
      carouselLimit: 1.25,
      slidesToScroll: 1,
      posterLimit: 2.5,
      posterToScroll: 2,
      descriptionLines: 2,
      roundArrows: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      showCarouselArrows: false,
      showTileMetadataOnHover: false,
      useMobileUpcomingOverlay: true,
      fycLogoHeight: 50,
      useScrollableDiv: true,
    },
  };
  return breakpoints;
})();

function normalizeVideos({
  isAuthenticated,
  preauthorizedResources,
  videos,
  collectionType,
  showTileMetadataOnHover,
  panelId,
}) {
  const videoItems = videos?.map(v => {
    if (!v) return null;
    const data = { ...v };
    data.isAuthenticated = !!isAuthenticated;
    data.preauthorizedResources = preauthorizedResources;
    data.isSeriesPromo = collectionType === 'seriespromo';
    data.showTileMetadataOnHover = showTileMetadataOnHover;
    data.panelId = panelId;
    return data;
  });
  return _.filter(videoItems, v => v);
}

export function getPanelProps(props) {
  const {
    accessToken,
    breakpoint,
    deviceId,
    panel,
    showcase,
    useShowcaseFallback,
    globalVideoStartedPlayback,
    favoritesManager,
    authSuccess,
    coordinates,
  } = props;
  return {
    accessToken,
    breakpoint,
    calculateValidFor: calculateNextRefreshForPanel,
    'data-test': 'channel-content',
    deviceId,
    headerGenerator: helperGetApiHeaders,
    panelData: panel,
    panelId: panel?.id,
    pageType: 'upNext',
    propsToWatch: {
      authSuccess,
      coordinates,
      favorites: favoritesManager?.favorites,
      globalVideoStartedPlayback,
      member: panel?.member,
      showcaseId: showcase?.id,
      useShowcaseFallback,
      // TODO support playerbanner in the future
      // userViewedPlayerBanner,
    },
    routeGenerator: helperConvertApiRoute,
  };
}

export function callCollectionImpressionEvent(props) {
  const { collectionImpressionCalled, account, visibleCollections } = props;
  getTealiumRedesignEvents({ account }).collectionImpression({ visibleCollections });
  collectionImpressionCalled();
}

function addSeeMoreItem({ items, collectionCTA, isFirst }) {
  const url = _.get(collectionCTA, '0.url');
  if (!url || isFirst) return items;
  const label = _.get(collectionCTA, '0.label', 'See All');
  const seeMoreItem = {
    '@type': 'seeMore',
    id: 'seeMore',
    headline: label,
    link: collectionCTA,
  };
  return _.concat(items, seeMoreItem);
}

function getChannelIndex(channelIds, currentPanelId) {
  return _.findIndex(channelIds, id => id === currentPanelId) + 1;
}

function onSelection({ panel, itemIdx, setRecommendationId }) {
  const featuredTitle = panel?.headline || '';
  _.set(window, 'context.segment.home.sourceName', featuredTitle);
  const selectedItem = itemIdx !== null ? itemIdx + 1 : 0;
  const data = {
    title: featuredTitle,
    collectionType: panel?.collectionType,
    items: panel?.items,
    selectedItem,
  };
  const recommendationIdData = getRecommendationIdDataForItem(panel?.items, selectedItem);
  setRecommendationId(recommendationIdData);
  setTealiumSourceCollectionGlobal(data);
}

function tealiumTileClick(props) {
  const { item, idx: index, account, visibleCollections, panel, panels, channelOrder } = props;
  onSelection(props);
  const totalCollections = _.get(panels, 'totalItems');
  const totalItems = _.get(panel, 'items.totalItems');
  const seriesName = _.get(item, 'seriesName');
  const name = _.get(item, 'name');
  const subtitle = _.get(homeCarouselVideoProcessor({ video: item }), 'subtitleText');
  callCollectionImpressionEvent(props);
  const cachedVerticalPosition = _.get(
    visibleCollections,
    `${panel?.headline}.verticalPosition`,
    null,
  );
  const verticalPosition = cachedVerticalPosition || getChannelIndex(channelOrder, panel?.id);
  getTealiumRedesignEvents({ account }).collectionItemSelect({
    account,
    state: 'static image',
    containerTitle: panel?.headline,
    verticalPosition,
    horizontalPosition: `${index + 1}`,
    title: seriesName || name,
    subtitle,
    cc: 'cc off',
    volume: 'volume off',
    collectionTitle: panel?.headline,
    collectionType: panel?.headline,
    playlistLength: totalItems,
    collectionCount: totalCollections,
    item,
    visibleCollections,
  });
}

function getCollectionName(pathname) {
  const collections = _.get(window.ExternalConfig, 'navigation', []);
  const rootCollection = _.find(collections, { link: '/' });
  let collectionData = null;
  if (pathname === '/' && rootCollection) {
    collectionData = rootCollection;
  } else {
    collectionData = _.find(collections, c => c.link !== '/' && _.includes(pathname, c.link));
  }
  return _.get(collectionData, 'label');
}

export function getTileCarouselProps(props) {
  const {
    account,
    isAuthenticated,
    disableScroll,
    breakpoint,
    panel,
    preauthorizedResources,
    idx,
    updateVisibleSlides,
    location,
    headline,
    panels,
    showcase,
    setFreemiumModalVisibility,
    favoritesManager,
    id,
  } = props;
  const onTileClick = tileProps => {
    const { item } = tileProps;
    const isLiveItem = isLive(_.get(item, '@type'));
    const isLiveNow = _.get(item, 'isLiveNow', false);
    const { id, callSign } = item;
    // Revisit: there's no way to identify bonus feeds right now, so we're using
    // this for all live sports feeds(?)
    if (isSportsContent(item) && isLiveItem) {
      if (isLiveItem && !isLiveNow) return;
      setSuggestedChannel({ network: callSign, programId: id });
      helperGoToRelativeUrl(helperGetLiveUrl({ callSign }));
    }
    if (autoplayEnabled()) {
      const source = PAGE_COLLECTION;
      removeGlobalVideoPlayer({ source });
    }
    tealiumTileClick({ ...props, ...tileProps });
  };
  const {
    addCarouselPlaceholders,
    allowScrollLock,
    carouselLimit,
    roundArrows,
    slidesToScroll,
    descriptionLines,
    showCarouselArrows,
    showTileMetadataOnHover,
    useScrollableDiv,
  } = BREAKPOINTS[breakpoint];
  const collectionMembers = [showcase, ...panels.member];
  const collection = { ...panels, member: collectionMembers };
  const areBadgesDisabled = !!panels?.member?.[id]?.areBadgesDisabled;
  return {
    addCarouselPlaceholders,
    allowScrollLock,
    collectionType: panel?.collectionType,
    collectionCount: getCollectionCount(collection),
    playlistLength: _.get(panel, 'items.member.length', 0),
    collectionName: getCollectionName(location?.pathname),
    collectionVerticalPosition: getCollectionPosition(collection, idx, headline),
    beforeChange: updateVisibleSlides,
    breakpoint,
    descriptionLines,
    disableScroll,
    favoritesManager,
    featuredTitleImage: panel?.images?.header?.raw,
    headline: panel?.headline,
    isFirst: false,
    isHoverStateV2: true,
    isLiveTileV2: false,
    isInfinite: false,
    isPosters: false,
    items: addSeeMoreItem({
      items: normalizeVideos({
        isAuthenticated,
        preauthorizedResources,
        videos: panel?.items?.member,
        panelId: panel?.id,
        collectionType: panel?.collectionType,
        showTileMetadataOnHover,
      }),
      collectionCTA: panel?.collectionCTA,
    }),
    noPreview: false,
    onTileClick,
    // overrideTheme: isLatestEpisodes ? LatestEpisodes : null,
    panel: { ...panel, areBadgesDisabled },
    panelIdx: idx,
    processor: item =>
      homeCarouselVideoProcessor({
        account,
        collectionType: panel?.collectionType,
        displayTemplate: panel?.displayTemplate,
        isHoverStateV2: true,
        isMobile: isMobileBreakpoint(breakpoint) || breakpoint === SITE_PLATFORM_TABLET,
        isPosters: false,
        panelType: panel?.panelType,
        video: item,
      }),
    renderImage: image =>
      renderImage({
        breakpoint,
        imageType: COLLECTION_THUMBNAIL_IMAGE,
        ...image,
      }),
    roundArrows,
    setFreemiumModalVisibility,
    shouldDisplayNetworkLogo: thumbnailNetworkLogoEnabled(),
    shouldInvertTitleOrder: item => {
      if (panel?.collectionType === 'localClips') return false;
      return isClip(item.videoType);
    },
    showCarouselArrows,
    slidesToScroll,
    slidesToShow: carouselLimit,
    theme: CarouselTheme,
    tileTheme: CarouselTileTheme,
    updateVisibleSlides,
    useScrollableDiv,
    subtitleLines: null,
  };
}

export function getPosterCarouselProps(props) {
  const {
    isAuthenticated,
    disableScroll,
    breakpoint,
    panel,
    preauthorizedResources,
    idx,
    updateVisibleSlides,
    location,
    headline,
    panels,
    showcase,
    setFreemiumModalVisibility,
    favoritesManager,
  } = props;
  const onTileClick = tileProps => {
    const { item } = tileProps;
    const isLiveItem = isLive(_.get(item, '@type'));
    const isLiveNow = _.get(item, 'isLiveNow', false);
    const { id, callSign } = item;
    // Revisit: there's no way to identify bonus feeds right now, so we're using
    // this for all live sports feeds(?)
    if (isSportsContent(item) && isLiveItem) {
      if (isLiveItem && !isLiveNow) return;
      setSuggestedChannel({ network: callSign, programId: id });
      helperGoToRelativeUrl(helperGetLiveUrl({ callSign }));
    }
    if (autoplayEnabled()) {
      const source = PAGE_COLLECTION;
      removeGlobalVideoPlayer({ source });
    }
    tealiumTileClick({ ...props, ...tileProps });
  };
  const {
    addCarouselPlaceholders,
    allowScrollLock,
    roundArrows,
    posterLimit,
    posterToScroll,
    descriptionLines,
    showCarouselArrows,
    showTileMetadataOnHover,
    useScrollableDiv,
  } = BREAKPOINTS[breakpoint];
  const collectionMembers = [showcase, ...panels.member];
  const collection = { ...panels, member: collectionMembers };
  return {
    addCarouselPlaceholders,
    allowScrollLock,
    collectionType: panel?.collectionType,
    collectionCount: getCollectionCount(collection),
    playlistLength: _.get(panel, 'items.member.length', 0),
    collectionName: getCollectionName(location?.pathname),
    collectionVerticalPosition: getCollectionPosition(collection, idx, headline),
    beforeChange: updateVisibleSlides,
    breakpoint,
    descriptionLines,
    disableScroll,
    favoritesManager,
    featuredTitleImage: panel?.images?.header?.raw,
    headline: panel?.headline,
    isFirst: false,
    isHoverStateV2: true,
    isLiveTileV2: false,
    isInfinite: false,
    isPosters: true,
    items: addSeeMoreItem({
      items: normalizeVideos({
        isAuthenticated,
        preauthorizedResources,
        videos: panel?.items?.member,
        panelId: panel?.id,
        collectionType: panel?.collectionType,
        showTileMetadataOnHover,
      }),
      collectionCTA: panel?.collectionCTA,
    }),
    noPreview: false,
    onTileClick,
    // overrideTheme: isLatestEpisodes ? LatestEpisodes : null,
    panel,
    panelIdx: idx,
    processor: item =>
      homeCarouselVideoProcessor({
        collectionType: panel?.collectionType,
        displayTemplate: panel?.displayTemplate,
        isHoverStateV2: true,
        isMobile: isMobileBreakpoint(breakpoint) || breakpoint === SITE_PLATFORM_TABLET,
        isPosters: true,
        panelType: panel?.panelType,
        video: item,
      }),
    renderImage: image =>
      renderImage({
        breakpoint,
        imageType: COLLECTION_THUMBNAIL_IMAGE,
        ...image,
      }),
    responsive: defaultPosterResponsiveness,
    roundArrows,
    setFreemiumModalVisibility,
    shouldDisplayNetworkLogo: thumbnailNetworkLogoEnabled(),
    shouldInvertTitleOrder: item => {
      if (panel?.collectionType === 'localClips') return false;
      return isClip(item.videoType);
    },
    showCarouselArrows,
    slidesToScroll: posterToScroll,
    slidesToShow: posterLimit,
    theme: CarouselPosterStyle,
    tileTheme: CarouselTileTheme,
    updateVisibleSlides,
    useScrollableDiv,
    subtitleLines: null,
  };
}
